<script setup lang="ts">
import { computed, ref, watch } from 'vue'
/** POST /api-keys. Two steps: explicit scope + name + expiry, then the plaintext shown once. */
import Dialog from '~/components/ui/Dialog.vue'
import Field from '~/components/ui/Field.vue'
import SelectField from '~/components/ui/SelectField.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import ParamRow from '~/components/inspector/ParamRow.vue'

const props = defineProps<{ created?: { name: string; plaintext: string; expires: string; scope: string; call?: string } | null; deployments: { id: string; name: string; slug: string }[]; initialSlug?: string; loading?: boolean; failure?: string }>()
defineEmits<{ close: []; submit: [payload: Record<string, unknown>] }>()
const name = ref('')
const expiry = ref('never')
const scopeKind = ref('deployment')
const deploymentId = ref('')
watch(() => props.deployments, (deployments) => {
  if (!deploymentId.value && props.initialSlug) deploymentId.value = deployments.find((entry) => entry.slug === props.initialSlug)?.id ?? ''
}, { immediate: true })
/* A key for one deployment is named after it until the customer writes a name of their own, so the
   form a newcomer opens from a deployment can be sent as it stands (W9-PORTAL-UX). */
const nameTouched = ref(false)
watch([deploymentId, scopeKind], () => {
  if (nameTouched.value) return
  const chosen = props.deployments.find((entry) => entry.id === deploymentId.value)
  name.value = scopeKind.value === 'deployment' && chosen ? `${chosen.slug}-key` : ''
}, { immediate: true })
const scopeOptions = [{ value: 'deployment', label: 'One deployment · invoke only' }, { value: 'organization', label: 'Whole organisation · all API resources' }]
const deploymentOptions = computed(() => [{ value: '', label: 'Choose a deployment' }, ...props.deployments.map((entry) => ({ value: entry.id, label: `${entry.name} · /${entry.slug}` }))])
const canCreate = computed(() => name.value.trim() && (scopeKind.value === 'organization' || deploymentId.value))
const scope = computed(() => ({ kind: scopeKind.value, deploymentId: scopeKind.value === 'deployment' ? deploymentId.value : null }))
const options = [{ value: 'never', label: 'Never expires' }, { value: '30', label: 'In 30 days' }, { value: '90', label: 'In 90 days' }, { value: '365', label: 'In 365 days' }]
async function copyText(text: string) { try { await navigator.clipboard.writeText(text) } catch { toast.error('Copy failed. Select and copy the key manually.') } }
</script>

<template>
  <Dialog :title="created ? 'Key created' : 'Create an API key'" :meta="created ? 'shown once' : scopeKind === 'deployment' ? 'one deployment' : 'whole organisation'" :width="480" @close="$emit('close')">
    <template #icon><Icon name="key" /></template>

    <template v-if="!created">
      <div class="flex items-start gap-2 rounded-md border border-warning/40 px-2.5 py-2 text-xs"><Icon name="alert" :size="12" class="mt-0.5 shrink-0 text-warning" /><span>A key is a secret. Call the address from your server, never from a web page: anyone who reads the page can spend your quota.</span></div>
      <SelectField v-model="scopeKind" label="Access" :options="scopeOptions" hint="Cannot be widened later · issue another key to change access" />
      <p v-if="failure && scopeKind === 'deployment'" role="alert" class="text-xs text-destructive">{{ failure }}</p>
      <p v-else-if="loading && scopeKind === 'deployment'" class="text-xs text-muted-foreground">Loading deployments…</p>
      <p v-else-if="scopeKind === 'deployment' && !deployments.length" class="text-xs text-muted-foreground">This project has no deployments yet. Deploy a model first, or give the key access to the whole organisation.</p>
      <SelectField v-if="scopeKind === 'deployment' && deployments.length" v-model="deploymentId" label="Deployment" :options="deploymentOptions" hint="Chat and predictions only · no management or access to other deployments" />
      <Field v-model="name" label="Name" placeholder="edge-worker" hint="Names the key in the list and in every audit entry it writes" @update:model-value="nameTouched = true" />
      <SelectField v-model="expiry" label="Expires" :options="options" hint="Cannot be changed later · create another key and revoke this one" />
    </template>

    <template v-else>
      <div class="flex flex-col gap-[var(--measure-7px)] rounded-md border border-border p-2.5">
        <ParamRow label="Access" :value="created.scope" />
        <ParamRow label="Name" :value="created.name" />
        <ParamRow label="Expires" :value="created.expires" />
      </div>
      <div class="flex items-center gap-2 rounded-md border border-primary/40 bg-sidebar-accent px-2.5 py-2">
        <span class="num min-w-0 flex-1 truncate text-xs">{{ created.plaintext }}</span>
        <ActionButton label="Copy" @click="created && copyText(created.plaintext)"><template #icon><Icon name="copy" /></template></ActionButton>
      </div>
      <span class="num text-[length:var(--type-fine)] text-muted-foreground">Store it now · after this dialog only the prefix is shown</span>
      <!-- The call this key makes, with the key already in it: the example on the deployment page
           carries a placeholder, and the one moment the key is readable is the moment to use it. -->
      <div v-if="created.call" class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs text-muted-foreground">Call the deployment with it</span>
          <ActionButton label="Copy request" size="xs" @click="created?.call && copyText(created.call)"><template #icon><Icon name="copy" /></template></ActionButton>
        </div>
        <code class="num block overflow-x-auto whitespace-pre rounded-md border border-border bg-muted/40 px-2.5 py-2 text-[length:var(--type-meta)] text-muted-foreground">{{ created.call }}</code>
      </div>
    </template>

    <template #actions>
      <template v-if="!created">
        <ActionButton label="Cancel" @click="$emit('close')" />
        <ActionButton label="Create the key" variant="primary" :disabled="!canCreate" @click="$emit('submit', { name: name.trim(), expiry, scope })"><template #icon><Icon name="key" /></template></ActionButton>
      </template>
      <ActionButton v-else label="Done" @click="$emit('close')" />
    </template>
  </Dialog>
</template>
